import type React from "react";
import { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Switch,
  FormControlLabel,
  Paper,
} from "@mui/material";
import {
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import apiService from "../../../service/Api";
import { Photo, PhotoSection } from "../../../types/venue";
import { VenueDocument } from "../types/venues.types";

interface ViewVenuePhotosProps {
  files: VenueDocument[];
  document_type: string;
}

const ViewVenuePhotos: React.FC<ViewVenuePhotosProps> = ({
  files,
  document_type,
}) => {
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [useAsCover, setUseAsCover] = useState(false);

  // Capitalize document_type for display (e.g., "venue_photos" -> "Venue Photos")
  const formatTitle = (type: string) =>
    type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  // Filter files based on the provided document_type
  const photoSections: PhotoSection[] = [
    {
      title: formatTitle(document_type),
      photos: files
        .filter((document) => document.file_type === document_type)
        .map((document) => ({
          id: document.id,
          src: document.file,
          alt: `${formatTitle(document_type)} ${document.id}`,
        })),
    },
  ];

  // Flatten photos from all sections
  const allPhotos: Photo[] = photoSections.flatMap((section) => section.photos);

  const handlePhotoClick = (photo: Photo) => {
    const photoIndex = allPhotos.findIndex((p) => p.id === photo.id);
    setCurrentPhotoIndex(photoIndex);
    setSelectedPhoto(photo);
  };

  const handleCloseModal = () => {
    setSelectedPhoto(null);
    setUseAsCover(false);
  };

  const handlePrevPhoto = () => {
    const newIndex =
      currentPhotoIndex > 0 ? currentPhotoIndex - 1 : allPhotos.length - 1;
    setCurrentPhotoIndex(newIndex);
    setSelectedPhoto(allPhotos[newIndex]);
  };

  const handleNextPhoto = () => {
    const newIndex =
      currentPhotoIndex < allPhotos.length - 1 ? currentPhotoIndex + 1 : 0;
    setCurrentPhotoIndex(newIndex);
    setSelectedPhoto(allPhotos[newIndex]);
  };

  const handleUseAsCoverChange = async (checked: boolean) => {
    if (!selectedPhoto) {
      toast.error("Invalid venue or photo selection", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        style: {
          backgroundColor: "#fff",
          color: "#333",
          border: "1px solid #C29363",
          zIndex: 9999,
        },
      });
      setUseAsCover(false);
      return;
    }

    setUseAsCover(checked);
    if (checked) {
      try {
        await apiService.post(`/api/venues/document/change-cover/`, {
          id: selectedPhoto.id,
        });
        toast.success("Cover photo updated successfully", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
          style: {
            backgroundColor: "#fff",
            color: "#333",
            border: "1px solid #C29363",
            zIndex: 9999,
          },
        });
      } catch (err) {
        let errorMessage = "Failed to update cover photo.";
        const errorData = (
          err as { response?: { data?: { detail?: string; message?: string } } }
        )?.response?.data;
        if (errorData?.detail) {
          errorMessage = errorData.detail;
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        }
        console.error("Error updating cover photo:", err);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
          style: {
            backgroundColor: "#fff",
            color: "#333",
            border: "1px solid #C29363",
            zIndex: 9999,
          },
        });
        setUseAsCover(false);
      }
    }
  };

  if (!allPhotos.length) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No {formatTitle(document_type).toLowerCase()} available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, minHeight: "100vh" }}>
      <Grid container spacing={2}>
        {photoSections.map((section) => (
          <Grid size={{ xs: 6, md: 6 }} key={section.title}>
            <Paper sx={{ p: 2, height: "100%", boxShadow: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#333" }}
                >
                  {section.title}
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {section.photos.map((photo) => (
                  <Grid size={{ xs: 6, sm: 4 }} key={photo.id}>
                    <Box
                      component="img"
                      src={photo.src}
                      alt={photo.alt}
                      sx={{
                        width: "100%",
                        height: 120,
                        objectFit: "cover",
                        borderRadius: 1,
                        cursor: "pointer",
                        transition: "transform 0.2s ease-in-out",
                        "&:hover": {
                          transform: "scale(1.02)",
                        },
                      }}
                      onClick={() => handlePhotoClick(photo)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={!!selectedPhoto}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "white",
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {formatTitle(document_type)}
          </Typography>
          <IconButton onClick={handleCloseModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, position: "relative" }}>
          {selectedPhoto && (
            <>
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <IconButton
                  onClick={handlePrevPhoto}
                  sx={{
                    position: "absolute",
                    left: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor: "white",
                    boxShadow: 2,
                    zIndex: 1,
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>

                <Box
                  component="img"
                  src={selectedPhoto.src}
                  alt={selectedPhoto.alt}
                  sx={{
                    width: "100%",
                    maxHeight: 400,
                    objectFit: "contain",
                  }}
                />

                <IconButton
                  onClick={handleNextPhoto}
                  sx={{
                    position: "absolute",
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor: "white",
                    boxShadow: 2,
                    zIndex: 1,
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>

              <Box sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useAsCover}
                      onChange={(e) => handleUseAsCoverChange(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Use as cover"
                  sx={{ color: "#666" }}
                />
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ViewVenuePhotos;
